import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentPage,
    getLocation,
    getLocationPathAndQuery,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getHomePageUrl, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import Hidden from "@insite/mobius/Hidden";
import { LinkProps } from "@insite/mobius/Link";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const locationPathAndQuery = getLocationPathAndQuery(state);
    let productPathAndQuery;
    if (locationPathAndQuery.includes("/Product/")) {
        productPathAndQuery = locationPathAndQuery;
    }
    const location = getLocation(state);
    let productPath;
    let isChild = false;
    if (location.pathname.includes("/Product/")) {
        productPath = location.pathname;
        if (location.search.length > 0) {
            isChild = true;
        }
    }
    let productState;
    if (productPath && productPath.length > 0) {
        productState = getProductStateByPath(state, productPath);
    }
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
        productPathAndQuery,
        productPath,
        productState,
        isChild,
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const PageBreadcrumbs = (props: Props) => {
    const { linksState, nodeId, homePageUrl, productPathAndQuery, productPath, productState } = props;

    let links = props.links || generateLinksFrom(linksState, nodeId, homePageUrl);
    if (productPath && productState && productPathAndQuery) {
        const linkIndex = links.length - 1;
        if (linkIndex > -1) {
            if (productState.value?.variantTraits) {
                const filteredVariantTraits = productState.value!.variantTraits?.filter(
                    vt => vt.name !== "Parent Child Connector",
                );
                // this code handles pages with connected products
                if (filteredVariantTraits!.length === 0 && productPathAndQuery!.includes("?option=")) {
                    const link = links[linkIndex];
                    const newBottomLevelLink = { children: link.children, href: productPath };
                    const linksWithoutBottomLevelLink = [...links].splice(0, links.length - 1);
                    links = [...linksWithoutBottomLevelLink, newBottomLevelLink];
                }
                // this code handles pages with variant option selection
                if (filteredVariantTraits!.length > 0) {
                    const link = links[linkIndex];
                    const newBottomLevelLink = { children: link.children, href: productPath };
                    const linksWithoutBottomLevelLink = [...links].splice(0, links.length - 1);
                    links = [...linksWithoutBottomLevelLink, newBottomLevelLink];
                }
            }
        }
    }

    let mobileLinks = new Array<LinkProps>();
    if (links.length > 0 && links.some(o => o.type === "HomePage" || o.type === undefined)) {
        // we need to always direct to the language-specific homepage URL
        const homePageLink = { children: translate("Home_Breadcrumb"), href: homePageUrl };
        const linksWithoutHomePageLink = [...links].splice(1, links.length);
        links = [homePageLink, ...linksWithoutHomePageLink];
        mobileLinks = links;
    }
    if (links.length > 1) {
        mobileLinks = new Array<LinkProps>();
        mobileLinks.push(links[links.length - (props.isChild ? 1 : 2)]);
    }
    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, props.extendedStyles);
    return (
        <>
            <Hidden above="sm">
                <Breadcrumbs links={mobileLinks} data-test-selector="pageBreadcrumbs" {...styles.breadcrumbs} />
            </Hidden>
            <Hidden below="md">
                <Breadcrumbs links={links} data-test-selector="pageBreadcrumbs" {...styles.breadcrumbs} />
            </Hidden>
        </>
    );
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);
    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));

    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));
        if (!isVmiPageType || !isVmiExcludePageType) {
            links.unshift({ children: currentLink.title, href: currentLink.url, type: currentLink.type });
        }

        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
